import { useState, useCallback, useMemo, useRef } from "react";
import { localStorageAsync } from "#app/lib/local-storage";

function readSync<T extends object>(key: string, defaults: T): T {
  if (typeof window === "undefined") return defaults;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return defaults;
    return { ...defaults, ...JSON.parse(raw) };
  } catch {
    return defaults;
  }
}

/**
 * Generic hook for state persisted to localStorage. The initial state is
 * seeded synchronously so the first paint already reflects the persisted
 * value — no flicker from defaults to stored value.
 *
 * Dynamic keys are supported: when `key` changes (e.g. a workspace-scoped
 * key after switching workspaces), state re-seeds from the new key's stored
 * value during the same render — no flash of the previous key's state.
 */
export function usePersistedState<T extends object>(
  key: string,
  defaults: T,
): { state: T; update: (patch: Partial<T>) => void; isLoaded: boolean } {
  const [state, setState] = useState<T>(() => readSync(key, defaults));

  // Re-seed during render when the key changes (not `useResetOnChange` —
  // that fires on mount too, which would double-render every consumer).
  const prevKeyRef = useRef(key);
  if (key !== prevKeyRef.current) {
    prevKeyRef.current = key;
    setState(readSync(key, defaults));
  }

  const update = useCallback(
    (patch: Partial<T>) => {
      setState((prev) => {
        const next = { ...prev, ...patch };
        localStorageAsync.setItem(key, JSON.stringify(next)).catch(() => {});
        return next;
      });
    },
    [key],
  );

  return useMemo(() => ({ state, update, isLoaded: true }), [state, update]);
}
